export const PAYMENT_PROVIDERS = ['Stripe', 'Square', 'PayPal'] as const
export const PAYMENT_PROVIDER_STATUSES = ['Connected', 'Not connected', 'Pending'] as const
export const PAYMENT_CONNECTION_VERIFICATION_STATUSES = ['Verified', 'Pending', 'Unavailable'] as const
export const PAYMENT_ENVIRONMENTS = ['sandbox', 'live'] as const
export const PAYMENT_SESSION_STATUSES = ['Created', 'Pending', 'Succeeded', 'Failed', 'Canceled', 'Needs review'] as const
export const PAYMENT_FEE_COLLECTION_METHODS = ['automatic', 'tracked'] as const

export type PaymentProviderName = (typeof PAYMENT_PROVIDERS)[number]
export type PaymentProviderStatus = (typeof PAYMENT_PROVIDER_STATUSES)[number]
export type PaymentConnectionVerificationStatus = (typeof PAYMENT_CONNECTION_VERIFICATION_STATUSES)[number]
export type PaymentEnvironment = (typeof PAYMENT_ENVIRONMENTS)[number]
export type PaymentSessionStatus = (typeof PAYMENT_SESSION_STATUSES)[number]
export type PaymentFeeCollectionMethod = (typeof PAYMENT_FEE_COLLECTION_METHODS)[number]
export type SupportedPaymentFrequency = 'One-time' | 'Monthly'

/** Square and PayPal currently create one-time checkout objects in KindLumen. */
export function paymentProviderSupportsFrequency(
  provider: PaymentProviderName | null | undefined,
  frequency: SupportedPaymentFrequency,
) {
  return frequency === 'One-time' || provider === 'Stripe'
}

// ---------------------------------------------------------------------------
// KindLumen's pricing model
// ---------------------------------------------------------------------------
// KindLumen is free for organizations: there is no subscription, no setup fee,
// and no platform fee charged to the org. The ONLY money KindLumen ever
// collects is the optional donor contribution below, and only when a donor
// ticks "Cover transaction costs" — never out of the organization's donation.
//
// Rates are expressed in integer BASIS POINTS (1 bp = 0.01%) rather than
// floats. Every amount in this module is integer cents. Nothing here may use
// floating-point currency arithmetic: a payment total that is off by a cent is
// a real defect, and 0.029 is not exactly representable in binary floating
// point.

/** KindLumen's share when (and only when) the donor covers transaction costs: 1%. */
export const KINDLUMEN_SUPPORT_BPS = 100

/**
 * Stripe's standard published rate for US domestic card charges: 2.9% + 30¢.
 *
 * This is the DEFAULT, not a universal truth. International cards, currency
 * conversion, ACH, and several local payment methods all price differently, and
 * Stripe applies its percentage to the amount actually processed. The rate is
 * therefore threaded through every calculation as a parameter (see
 * `ProcessingRate`) so a per-method or per-country schedule can be introduced
 * later without touching the math — and so the deployment can override it from
 * configuration when an org is on nonprofit or custom Stripe pricing.
 */
export const DEFAULT_STRIPE_PROCESSING_BPS = 290
export const DEFAULT_STRIPE_PROCESSING_FIXED_CENTS = 30

export type ProcessingRate = {
  /** Percentage the processor takes of the final captured amount, in basis points. */
  percentageBps: number
  /** Flat per-transaction processor charge, in integer cents. */
  fixedCents: number
}

export const DEFAULT_PROCESSING_RATE: ProcessingRate = {
  percentageBps: DEFAULT_STRIPE_PROCESSING_BPS,
  fixedCents: DEFAULT_STRIPE_PROCESSING_FIXED_CENTS,
}

const BPS_DENOMINATOR = 10_000

export function clampMoneyCents(value: number) {
  if (!Number.isFinite(value)) {
    return 0
  }

  return Math.max(0, Math.round(value))
}

export function getNetCapturedAmountCents(amountCents: number, refundedAmountCents = 0) {
  return Math.max(clampMoneyCents(amountCents) - clampMoneyCents(refundedAmountCents), 0)
}

function normalizeProcessingRate(rate: ProcessingRate | undefined): ProcessingRate {
  const percentageBps = rate ? Math.round(rate.percentageBps) : DEFAULT_PROCESSING_RATE.percentageBps
  const fixedCents = rate ? Math.round(rate.fixedCents) : DEFAULT_PROCESSING_RATE.fixedCents

  // A rate at or above 100% makes the gross-up divide by zero or go negative —
  // there is no charge large enough to net the intended amount. Refuse rather
  // than silently produce a nonsense total.
  if (!Number.isFinite(percentageBps) || percentageBps < 0 || percentageBps >= BPS_DENOMINATOR) {
    return DEFAULT_PROCESSING_RATE
  }

  if (!Number.isFinite(fixedCents) || fixedCents < 0) {
    return { percentageBps, fixedCents: DEFAULT_PROCESSING_RATE.fixedCents }
  }

  return { percentageBps, fixedCents }
}

/**
 * What the processor is expected to deduct from a captured charge.
 *
 * An ESTIMATE. The authoritative number is the Stripe balance transaction fee
 * recorded on the donation once the charge settles (`processorFeeCents`); this
 * only exists so the donor can be shown a total up front and so the gross-up
 * can be solved.
 */
export function estimateProcessingFeeCents(chargeAmountCents: number, rate?: ProcessingRate) {
  const charge = clampMoneyCents(chargeAmountCents)

  if (charge === 0) {
    return 0
  }

  const { percentageBps, fixedCents } = normalizeProcessingRate(rate)
  return Math.round((charge * percentageBps) / BPS_DENOMINATOR) + fixedCents
}

/** KindLumen's contribution (KINDLUMEN_SUPPORT_BPS) on the donor's intended gift, in integer cents. */
export function calculateKindlumenContributionCents(intendedAmountCents: number, supportBps = KINDLUMEN_SUPPORT_BPS) {
  const intended = clampMoneyCents(intendedAmountCents)
  const bps = Number.isFinite(supportBps) && supportBps > 0 ? Math.round(supportBps) : 0

  if (intended === 0 || bps === 0) {
    return 0
  }

  return Math.round((intended * bps) / BPS_DENOMINATOR)
}

export type DonationChargeBreakdown = {
  /** What the donor chose to give — what the organization is meant to receive. */
  intendedAmountCents: number
  /** Whether the donor opted to cover transaction costs. */
  donorCoversCosts: boolean
  /** What the donor's card is actually charged. */
  grossChargeAmountCents: number
  /** The donor's extra, split out: the part that absorbs the processor's cut. */
  processingCoverageCents: number
  /** The donor's extra, split out: KindLumen's contribution. */
  kindlumenContributionCents: number
  /** Estimated processor fee on the gross charge. Replaced by the real fee at settlement. */
  estimatedProcessingFeeCents: number
  /** Estimated amount the organization nets once the processor takes its cut. */
  estimatedOrganizationNetCents: number
  /** The single combined figure shown to the donor as "Transaction-cost coverage". */
  totalCoverageCents: number
  /** The processing rate the numbers above were solved against. */
  rate: ProcessingRate
}

/**
 * Solve the donor-facing charge for a donation.
 *
 * When the donor does NOT cover costs the charge is simply the gift: the
 * processor's fee comes out of it, the organization nets the remainder, and
 * KindLumen collects nothing at all.
 *
 * When the donor DOES cover costs the charge is grossed up so that, after the
 * processor takes its percentage of the FINAL amount, the organization still
 * receives the full intended gift and KindLumen receives exactly its share.
 * Adding a flat 2.9% to the gift would fall short, because Stripe charges its
 * percentage against the larger amount it actually processes:
 *
 *     contribution = intended * supportRate
 *     gross        = (intended + contribution + fixedFee) / (1 - percentageRate)
 *
 * Solved entirely in integer cents. The result is then verified against the
 * estimated fee and nudged up by a cent if rounding would otherwise leave the
 * organization a cent short — the org must never receive less than the donor
 * intended to give.
 */
export function calculateDonationCharge(args: {
  intendedAmountCents: number
  donorCoversCosts: boolean
  rate?: ProcessingRate
  supportBps?: number
  /** Organization policy, resolved by the server for real payments. */
  kindlumenFeeExempt?: boolean
}): DonationChargeBreakdown {
  const rate = normalizeProcessingRate(args.rate)
  const intendedAmountCents = clampMoneyCents(args.intendedAmountCents)

  if (intendedAmountCents === 0) {
    return {
      intendedAmountCents: 0,
      donorCoversCosts: false,
      grossChargeAmountCents: 0,
      processingCoverageCents: 0,
      kindlumenContributionCents: 0,
      estimatedProcessingFeeCents: 0,
      estimatedOrganizationNetCents: 0,
      totalCoverageCents: 0,
      rate,
    }
  }

  if (!args.donorCoversCosts) {
    // The donor pays exactly what they chose. The processor's fee reduces the
    // organization's payout, and KindLumen takes nothing — no application fee
    // is collected on this charge at all.
    const estimatedProcessingFeeCents = estimateProcessingFeeCents(intendedAmountCents, rate)

    return {
      intendedAmountCents,
      donorCoversCosts: false,
      grossChargeAmountCents: intendedAmountCents,
      processingCoverageCents: 0,
      kindlumenContributionCents: 0,
      estimatedProcessingFeeCents,
      estimatedOrganizationNetCents: Math.max(intendedAmountCents - estimatedProcessingFeeCents, 0),
      totalCoverageCents: 0,
      rate,
    }
  }

  const kindlumenContributionCents = calculateKindlumenContributionCents(
    intendedAmountCents,
    args.kindlumenFeeExempt === true ? 0 : args.supportBps ?? KINDLUMEN_SUPPORT_BPS,
  )

  const numeratorCents = intendedAmountCents + kindlumenContributionCents + rate.fixedCents
  let grossChargeAmountCents = Math.round(
    (numeratorCents * BPS_DENOMINATOR) / (BPS_DENOMINATOR - rate.percentageBps),
  )

  // Rounding the division can land a cent low. Walk up until the organization
  // provably nets the full intended gift after the estimated fee and
  // KindLumen's contribution. In practice this adjusts by 0 or 1 cent; the
  // bound keeps a pathological rate from spinning.
  for (let step = 0; step < 4; step += 1) {
    const netToOrganization =
      grossChargeAmountCents - estimateProcessingFeeCents(grossChargeAmountCents, rate) - kindlumenContributionCents

    if (netToOrganization >= intendedAmountCents) {
      break
    }

    grossChargeAmountCents += 1
  }

  const estimatedProcessingFeeCents = estimateProcessingFeeCents(grossChargeAmountCents, rate)
  const totalCoverageCents = Math.max(grossChargeAmountCents - intendedAmountCents, 0)

  return {
    intendedAmountCents,
    donorCoversCosts: true,
    grossChargeAmountCents,
    // The donor sees one combined figure; internally the extra is stored split
    // into the part that absorbs processing and KindLumen's contribution.
    processingCoverageCents: Math.max(totalCoverageCents - kindlumenContributionCents, 0),
    kindlumenContributionCents,
    estimatedProcessingFeeCents,
    estimatedOrganizationNetCents: Math.max(
      grossChargeAmountCents - estimatedProcessingFeeCents - kindlumenContributionCents,
      0,
    ),
    totalCoverageCents,
    rate,
  }
}

/**
 * KindLumen's contribution to give back when a donation is refunded.
 *
 * Proportional to how much of the charge was returned, capped at what was
 * collected. Note this describes what SHOULD be returned — Stripe only actually
 * reverses the application fee when the refund is issued with
 * `refund_application_fee`, so this figure is what KindLumen records, not proof
 * the money moved.
 */
export function calculatePlatformFeeRefundCents(args: {
  amountCents: number
  refundedAmountCents: number
  platformFeeCents: number
}) {
  const amountCents = clampMoneyCents(args.amountCents)
  const refundedAmountCents = Math.min(clampMoneyCents(args.refundedAmountCents), amountCents)
  const platformFeeCents = clampMoneyCents(args.platformFeeCents)

  if (amountCents === 0 || refundedAmountCents === 0 || platformFeeCents === 0) {
    return 0
  }

  return Math.min(platformFeeCents, Math.round((platformFeeCents * refundedAmountCents) / amountCents))
}
